import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import jwtDecode from 'jwt-decode';
import { Language } from '@data/sdk/match';
import { paths } from '../../../app/constants';
import i18n from '../../../app/i18n';
import { useAppUserStore } from '../../storage';
import { HTTPStatusCodes } from '../enums';
import { isMaintenanceMode } from '../helpers';
import { BEError, IToken, RefreshSessionBody } from '../types';

const authEndpoints = [
  /\/auth\/refresh/,
  /\/auth\/login/,
  /\/auth\/sign-up/,
  /\/invites\/token/,
  /\/invites\/accept/,
  /\/auth\/trial-manager-sign-up/,
  /\/auth\/anon\/sign-up/,
  /\/auth\/anon\/claim\//,
  /\/auth\/google-sign-up/,
  /\/auth\/google-login/,
  /\/auth\/set-password/,
  /\/profile\/passwords/,
];

type UserEntity = {
  firstName: string;
  jotSubmissionId?: string;
  language: Language;
  lastName: string;
  refreshToken: string;
  token: string;
};

const isRefreshUrl = (url?: string): boolean => /\/?match\/auth\/refresh/.test(url || '');

const isAuthEndpoint = (responseURL: string): boolean =>
  !!authEndpoints.find((endpoint) => endpoint.test(responseURL));

const refreshSession = (values: RefreshSessionBody): Promise<AxiosResponse<UserEntity>> =>
  axios.post('/auth/refresh', values, { baseURL: import.meta.env.VITE_API_BASE_URL });

const onRequest = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  const newConfig = { ...config };
  const { user } = useAppUserStore.getState();

  if (user?.token) {
    const { exp } = jwtDecode<IToken>(user.token);
    const tokenHasExpired = new Date().getTime() / 1000 > Number(exp) - 5;

    if (tokenHasExpired) {
      if (user?.refreshToken) {
        if (!isRefreshUrl(config.url)) {
          const { data } = await refreshSession({ refreshToken: user?.refreshToken });
          newConfig.headers.Authorization = data.token;

          const tokenData = jwtDecode<IToken>(data.token);
          useAppUserStore.setState({
            user: {
              ...data,
              currentRole: tokenData?.userData.roles[0],
              roles: tokenData.userData.roles,
            },
          });
        }
      } else {
        localStorage.clear();
        window.location.reload();
        return {
          ...config,
          cancelToken: new axios.CancelToken((cancel) => cancel('Cancel request')),
        };
      }
    }
    if (!tokenHasExpired && newConfig.headers) {
      newConfig.headers.Authorization = `${user.token}`;
    }
  }

  return newConfig;
};

const onResponse = async (res: AxiosResponse) => {
  const { data, request } = res;

  if (isAuthEndpoint(request.responseURL)) {
    const tokenData = jwtDecode<IToken>(data.token);
    useAppUserStore.setState({
      user: { ...data, currentRole: tokenData?.userData.roles[0], roles: tokenData.userData.roles },
    });
    i18n.changeLanguage(tokenData.userData.language ?? Language.En);
    return res;
  }

  return res;
};

const onResponseError = (error: AxiosError<BEError>): Promise<AxiosError> => {
  if (error?.response) {
    const {
      response: { data: errorData },
    } = error;
    const url = new URL(window.location.href);

    if (isMaintenanceMode(error) && !url.pathname.includes(paths.maintenance)) {
      const redirectPath = new URL(`/${paths.maintenance}`, window.location.origin);
      redirectPath.searchParams.set('redirectPath', url.pathname);
      window.location.href = redirectPath.toString();
    } else if (
      error?.response?.status === HTTPStatusCodes.UNAUTHORIZED ||
      isRefreshUrl(error?.response?.request?.responseURL)
    ) {
      localStorage.clear();
      // navigate to root path to prevent reload loop
      window.location.replace('/');
    }

    if (errorData) {
      if (errorData?.message === 'TrialNotProposed') {
        window.location.reload();
      }
      return Promise.reject(error);
    }
  }

  return Promise.reject(new Error('Something went wrong'));
};

export function axiosInterceptors(axiosInstance: AxiosInstance): AxiosInstance {
  axiosInstance.interceptors.request.use(onRequest);
  axiosInstance.interceptors.response.use(onResponse, onResponseError);
  return axiosInstance;
}
